import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Accordion, AccordionDetails, AccordionSummary, Button } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { iconSizes, s } from '@/theme/tokens';
import { DataStateSkeleton } from '@/ui/composites/DataStateSkeleton';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { Inline } from '@/ui/layout/Inline';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Chip,
  ExpandIcon,
  formatStatusLabel,
  getStatusTextColor,
  Link,
  Surface,
  Text,
  Tooltip,
  useTheme,
} from '@/ui/primitives';
import { buildSuiteDetails, useCopyPanel } from './resultsFileDetailUtils';
import {
  buildExecutionMetadataGridTemplate,
  EXECUTION_ROW_COLUMN_GAP,
  LATEST_CHIP_COLUMN_SX,
} from './resultsFileLayout';
import { ResultsFileOutputPanel } from './ResultsFileOutputPanel';
import { ResultsFileTestExecutionInlineList } from './ResultsFileTestExecutionInlineList';
import type { ExecutionRunDto, SuiteExecutionDto } from '@flowqa/shared';

export type ResultsFileSuiteExecutionPanelsSectionProps = {
  suiteExecutions: SuiteExecutionDto[];
  loading: boolean;
  executionRunId: string | null;
  executionRun: ExecutionRunDto | null;
  showExecutionRunSummary?: boolean;
  showSuitesHeading?: boolean;
};

export const ResultsFileSuiteExecutionPanelsSection = ({
  suiteExecutions,
  loading,
  executionRunId,
  executionRun,
  showExecutionRunSummary = true,
  showSuitesHeading = false,
}: ResultsFileSuiteExecutionPanelsSectionProps): JSX.Element => {
  const muiTheme = useTheme();
  const noDataLabel = 'No data';
  const emptyState = !loading && suiteExecutions.length === 0;
  const executionRunValue = executionRunId ?? noDataLabel;
  const executionRunStatus = executionRun?.status ?? null;
  const executionRunDurationLabel =
    executionRun?.durationMs !== null && executionRun?.durationMs !== undefined
      ? formatDuration(executionRun.durationMs)
      : noDataLabel;
  const executionRunTimestamp = executionRun?.startedAt ?? executionRun?.finishedAt ?? null;
  const executionRunTimestampLabel = executionRunTimestamp
    ? `${executionRunTimestamp.toLocaleTimeString()} ${executionRunTimestamp.toLocaleDateString()}`
    : null;
  const copyPanel = useCopyPanel();
  const [expandedSuites, setExpandedSuites] = useState<Set<string>>(new Set());
  const didAutoExpandSuites = useRef(false);
  useEffect(() => {
    if (didAutoExpandSuites.current || suiteExecutions.length !== 1 || expandedSuites.size > 0) {
      return;
    }
    setExpandedSuites(new Set([suiteExecutions[0].id]));
    didAutoExpandSuites.current = true;
  }, [expandedSuites.size, suiteExecutions]);
  const toggleSuite = (id: string, expanded: boolean): void => {
    setExpandedSuites((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };
  const expandAllSuites = (): void => {
    setExpandedSuites(new Set(suiteExecutions.map((suiteExecution) => suiteExecution.id)));
  };
  const collapseAllSuites = (): void => {
    setExpandedSuites(new Set());
  };
  const actionIconSize = s('2xl');
  const actionButtonSx = {
    width: actionIconSize,
    height: actionIconSize,
    minWidth: actionIconSize,
    minHeight: actionIconSize,
    padding: 0,
    borderRadius: s('sm'),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    color: 'text.primary',
    border: 'none',
    backgroundColor: 'transparent',
    '& .MuiSvgIcon-root': {
      fontSize: iconSizes.md,
      display: 'block',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  } as const;

  const renderExpandCollapseControls = (): JSX.Element => (
    <Inline gap="xs" wrap="nowrap" align="center">
      <Tooltip title="Expand" placement="top">
        <Button type="button" onClick={expandAllSuites} sx={actionButtonSx} aria-label="Expand">
          <OpenInFullIcon />
        </Button>
      </Tooltip>
      <Tooltip title="Collapse" placement="top">
        <Button type="button" onClick={collapseAllSuites} sx={actionButtonSx} aria-label="Collapse">
          <CloseFullscreenIcon />
        </Button>
      </Tooltip>
    </Inline>
  );
  const hasLatestSuiteChip = useMemo(
    () => suiteExecutions.some((execution) => execution.isLatest === true),
    [suiteExecutions],
  );
  const suiteRowGridTemplate = useMemo(() => {
    const rows = suiteExecutions.map((execution) => ({
      name: execution.suiteName ?? execution.name ?? 'Suite Execution',
      runLabel:
        execution.runNumber !== null && execution.runNumber !== undefined
          ? `#${String(execution.runNumber)}`
          : `#${noDataLabel}`,
      statusLabel: formatStatusLabel(execution.status),
      durationLabel:
        execution.durationMs !== null && execution.durationMs !== undefined
          ? formatDuration(execution.durationMs)
          : noDataLabel,
    }));
    return buildExecutionMetadataGridTemplate({
      theme: muiTheme,
      rows,
      hasLatestChip: hasLatestSuiteChip,
      nameTypography: 'subtitle1',
      fallbackNameWidthCh: 30,
      fallbackStatusWidthCh: 8,
      fallbackDurationWidthCh: 10,
      namePaddingPx: 18,
      runPaddingPx: 6,
      statusPaddingPx: 12,
      durationPaddingPx: 12,
    });
  }, [muiTheme, noDataLabel, suiteExecutions, hasLatestSuiteChip]);
  return (
    <Section paddingY="md">
      <Surface padding="lg" border>
        <Stack gap={showExecutionRunSummary ? 'md' : 'sm'}>
          {showSuitesHeading ? (
            <Stack
              direction="row"
              align="center"
              justify="space-between"
              sx={{ minWidth: 0, width: '100%' }}
            >
              <Text role="sectionTitle">Suites</Text>
              <Box sx={{ marginLeft: 'auto' }}>{renderExpandCollapseControls()}</Box>
            </Stack>
          ) : null}
          {showExecutionRunSummary ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: s('lg'),
                flexWrap: 'nowrap',
                minWidth: 0,
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: s('md'),
                  minWidth: 0,
                  flex: '0 1 auto',
                }}
              >
                <Text role="sectionTitle" sx={{ flexShrink: 0 }}>
                  Execution Run
                </Text>
                {executionRunId ? (
                  <Text
                    role="body"
                    color="textSecondary"
                    component="span"
                    sx={{
                      fontSize: (theme) => theme.typography.subtitle1.fontSize,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: '0 1 auto',
                      maxWidth: '32ch',
                    }}
                  >
                    <Link
                      component={RouterLink}
                      to={`/execution-runs/${executionRunId}`}
                      underline="hover"
                      color="inherit"
                      sx={{ cursor: 'pointer' }}
                    >
                      {executionRunValue}
                    </Link>
                  </Text>
                ) : (
                  <Text
                    role="body"
                    color="textSecondary"
                    sx={{
                      fontSize: (theme) => theme.typography.subtitle1.fontSize,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: '0 1 auto',
                      maxWidth: '32ch',
                    }}
                  >
                    {executionRunValue}
                  </Text>
                )}
                {executionRunStatus ? (
                  <Text
                    role="body"
                    component="span"
                    sx={{ color: (theme) => getStatusTextColor(theme, executionRunStatus) }}
                    transform="uppercase"
                  >
                    {formatStatusLabel(executionRunStatus)}
                  </Text>
                ) : null}
                {executionRunStatus ? (
                  <Text role="body" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                    {executionRunDurationLabel}
                  </Text>
                ) : null}
                {executionRunStatus && executionRunTimestampLabel ? (
                  <Text role="body" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                    {executionRunTimestampLabel}
                  </Text>
                ) : null}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: s('sm'),
                  flexShrink: 0,
                  marginLeft: 'auto',
                  justifyContent: 'flex-end',
                }}
              >
                {renderExpandCollapseControls()}
              </Box>
            </Box>
          ) : null}
          <Stack gap="xs" sx={{ marginTop: s('xs') }}>
            {suiteExecutions.map((execution, index) => {
              const suiteName = execution.suiteName ?? execution.name ?? 'Suite Execution';
              const runNumber =
                execution.runNumber !== null && execution.runNumber !== undefined
                  ? String(execution.runNumber)
                  : noDataLabel;
              const hasSuiteRunNumber =
                execution.runNumber !== null && execution.runNumber !== undefined;
              const durationLabel =
                execution.durationMs !== null && execution.durationMs !== undefined
                  ? formatDuration(execution.durationMs)
                  : noDataLabel;
              const suiteSlug = execution.suiteSlug ?? execution.suiteId;
              const suiteDetails = buildSuiteDetails(execution);
              const hasSuiteDetails = suiteDetails.length > 0;
              const isExpanded = expandedSuites.has(execution.id);
              return (
                <Box key={execution.id}>
                  <Accordion
                    disableGutters
                    elevation={0}
                    square
                    expanded={isExpanded}
                    onChange={(event, expanded) => {
                      const target = event.target as HTMLElement | null;
                      if (!target?.closest('.MuiAccordionSummary-expandIconWrapper')) {
                        return;
                      }
                      toggleSuite(execution.id, expanded);
                    }}
                    sx={{
                      backgroundColor: 'transparent',
                      '&::before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandIcon fontSize="small" />}
                      sx={{
                        padding: s('none'),
                        minHeight: 'auto',
                        flexDirection: 'row-reverse',
                        gap: s('sm'),
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        cursor: 'default !important',
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          marginRight: s('sm'),
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: 0,
                          alignItems: 'center',
                          width: '100%',
                          minWidth: 0,
                          flexGrow: 1,
                          userSelect: 'text',
                          WebkitUserSelect: 'text',
                          cursor: 'default !important',
                        },
                        '& .MuiAccordionSummary-content *': {
                          userSelect: 'text',
                          WebkitUserSelect: 'text',
                        },
                        '&.MuiButtonBase-root': {
                          userSelect: 'text',
                          WebkitUserSelect: 'text',
                          cursor: 'default !important',
                        },
                        '&:hover': {
                          cursor: 'default !important',
                        },
                      }}
                    >
                      <Box
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        sx={{ width: '100%' }}
                      >
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: suiteRowGridTemplate,
                            columnGap: EXECUTION_ROW_COLUMN_GAP,
                            alignItems: 'center',
                            width: 'fit-content',
                            maxWidth: '100%',
                            minWidth: 0,
                          }}
                        >
                          <Inline
                            gap="md"
                            wrap="nowrap"
                            sx={{ alignItems: 'center', minWidth: 0, overflow: 'hidden' }}
                          >
                            {suiteSlug ? (
                              <Link
                                component={RouterLink}
                                to={`/suites/${suiteSlug}`}
                                underline="hover"
                                color="inherit"
                                sx={{
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: 'block',
                                  fontSize: (theme) => theme.typography.subtitle1.fontSize,
                                  fontWeight: (theme) => theme.typography.fontWeightRegular,
                                }}
                              >
                                {suiteName}
                              </Link>
                            ) : (
                              <Text
                                role="body"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontSize: (theme) => theme.typography.subtitle1.fontSize,
                                  fontWeight: (theme) => theme.typography.fontWeightRegular,
                                }}
                              >
                                {suiteName}
                              </Text>
                            )}
                          </Inline>
                          <Inline
                            gap="none"
                            wrap="nowrap"
                            sx={{ alignItems: 'center', minWidth: 0 }}
                          >
                            <Text
                              role="body"
                              color="textSecondary"
                              component="span"
                              sx={{
                                whiteSpace: 'nowrap',
                                fontSize: (theme) => theme.typography.body2.fontSize,
                              }}
                            >
                              {hasSuiteRunNumber && suiteSlug ? (
                                <Link
                                  component={RouterLink}
                                  to={`/suites/${suiteSlug}/runs/${runNumber}`}
                                  underline="hover"
                                  color="inherit"
                                  sx={{ cursor: 'pointer' }}
                                >
                                  #{runNumber}
                                </Link>
                              ) : (
                                `#${runNumber}`
                              )}
                            </Text>
                          </Inline>
                          <Box sx={LATEST_CHIP_COLUMN_SX}>
                            {hasLatestSuiteChip ? (
                              <Chip
                                label="Latest"
                                tone="primary"
                                transform="uppercase"
                                size="xs"
                                sx={execution.isLatest ? undefined : { visibility: 'hidden' }}
                                aria-hidden={!execution.isLatest}
                              />
                            ) : null}
                          </Box>
                          <Text
                            role="body"
                            component="span"
                            sx={{ color: (theme) => getStatusTextColor(theme, execution.status) }}
                            transform="uppercase"
                          >
                            {formatStatusLabel(execution.status)}
                          </Text>
                          <Text role="body" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                            {durationLabel}
                          </Text>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ padding: s('none'), paddingTop: s('md'), paddingBottom: s('sm') }}
                    >
                      <Stack gap="sm">
                        {hasSuiteDetails ? (
                          <Accordion
                            disableGutters
                            elevation={0}
                            square
                            sx={{
                              backgroundColor: 'transparent',
                              '&::before': { display: 'none' },
                              marginLeft: s('2xl'),
                              width: `calc(100% - ${s('2xl')})`,
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandIcon fontSize="small" />}
                              sx={{
                                padding: s('none'),
                                minHeight: 'auto',
                                justifyContent: 'flex-start',
                                '&.MuiAccordionSummary-root': {
                                  minHeight: 0,
                                },
                                '&.MuiButtonBase-root': {
                                  padding: s('none'),
                                  minHeight: 0,
                                  cursor: 'default',
                                  pointerEvents: 'none',
                                },
                                '& .MuiAccordionSummary-content': {
                                  margin: 0,
                                  alignItems: 'center',
                                  flexGrow: 0,
                                  justifyContent: 'flex-start',
                                  pointerEvents: 'none',
                                },
                                '& .MuiAccordionSummary-content.Mui-expanded': {
                                  margin: 0,
                                },
                                '& .MuiAccordionSummary-expandIconWrapper': {
                                  marginLeft: s('md'),
                                  pointerEvents: 'auto',
                                },
                              }}
                            >
                              <Text role="label" color="textSecondary">
                                Output
                              </Text>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                padding: s('none'),
                                paddingTop: s('none'),
                                paddingBottom: s('md'),
                                width: '100%',
                              }}
                            >
                              <ResultsFileOutputPanel items={suiteDetails} onCopy={copyPanel} />
                            </AccordionDetails>
                          </Accordion>
                        ) : null}
                        {isExpanded ? (
                          <Box sx={{ paddingTop: s('xs') }}>
                            <ResultsFileTestExecutionInlineList
                              suiteExecutionId={execution.id}
                              resultsFileId={execution.resultsFileId ?? null}
                              projectId={execution.projectId}
                              organisationId={execution.organisationId}
                              rowColumnGap={EXECUTION_ROW_COLUMN_GAP}
                            />
                          </Box>
                        ) : null}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                  {index < suiteExecutions.length - 1 ? (
                    <Box sx={{ height: isExpanded ? s('lg') : s('md') }} />
                  ) : null}
                </Box>
              );
            })}
            {loading ? (
              <Surface padding="md" border>
                <DataStateSkeleton variant="flat" rows={3} padding="sm" />
              </Surface>
            ) : null}
            {emptyState ? (
              <Text role="body" color="textSecondary">
                No suite executions for this results file yet.
              </Text>
            ) : null}
          </Stack>
        </Stack>
      </Surface>
    </Section>
  );
};
